/**
 * Reads a named measurement from sensor.latestData.
 * Handles both legacy insigh.io key names and the new dynamic key system.
 *
 * Usage:
 *   readMeasurement(sensor, 'airTemp')   → number | null
 *   readMeasurement(sensor, 'humidity')  → number | null
 *   readMeasurement(sensor, 'windSpeed') → number | null
 */

// Logical measurement name → ordered list of candidate keys to try
const ALIASES = {
    airTemp: [
        'A.Temperature',        // WEATHER (Greek pilot)
        'havaSicaklik',         // insigh.io legacy
        'air_temp',
        'AirTemp',
        'Temperature',
        'temperature',
    ],
    humidity: [
        'board_humidity',       // WEATHER (Greek pilot)
        'havaNem',              // insigh.io legacy
        'Air_Humidity',
        'AirHumidity',
        'humidity',
        'Humidity',
        'RH',
    ],
    windSpeed: [
        'meter_0_1_east_wind_speed',   // WEATHER east component
        'meter_0_1_north_wind_speed',  // WEATHER north component (fallback)
        'WindSpeed',
        'wind_speed',
        'windSpeed',
    ],
    pressure: [
        'A_Pressure',
        'Pressure',
        'pressure',
        'AtmPressure',
    ],
    precipitation: [
        'Precipitation',
        'yagis',
        'rainfall',
        'Rainfall',
        'rain',
    ],
    soilMoisture: [
        'VWC_15cm',
        'VWC_50cm',
        'toprakNem',
        'soilMoisture',
        'SoilMoisture',
        'soil_moisture',
    ],
    soilTemp: [
        'S.Temperature_15cm',
        'S.Temperature_50cm',
        'toprakSicaklik',
        'soilTemp',
        'SoilTemp',
        'soil_temp',
    ],
    soilEC: [
        'S.Conductivity_15cm',
        'S.Conductivity_50cm',
        'SoilEC',
        'soilEC',
        'ec',
    ],
    ph: ['pH', 'ph', 'SoilPH'],
    waterEC: ['Conductivity', 'EC', 'ec'],
    dissolvedO2: ['DO', 'dissolvedO2', 'DissolvedOxygen'],
    orp: ['ORP', 'orp'],
    salinity: ['Salinity', 'salinity'],
    flow: ['Liter', 'Litre', 'liter', 'litre', 'FlowRate', 'flow'],
};

/**
 * Read a logical measurement from sensor.latestData.
 * @param {object} sensor  — sensor object with latestData: { [key]: number }
 * @param {string} name    — logical name from ALIASES above
 * @returns {number|null}
 */
export function readMeasurement(sensor, name) {
    const data = sensor?.latestData;
    if (!data || typeof data !== 'object') return null;

    const candidates = ALIASES[name];
    if (!candidates) return null;

    for (const key of candidates) {
        if (data[key] != null) return parseFloat(data[key]);
    }
    return null;
}

/**
 * Returns the best display value for a sensor — first non-null measurement.
 * Used for generic sensor cards where the exact measurement isn't known.
 */
export function primaryValue(sensor) {
    const data = sensor?.latestData;
    if (!data) return sensor?.latestValue ?? null;
    const vals = Object.values(data).filter(v => v != null && !isNaN(v));
    return vals.length > 0 ? parseFloat(vals[0]) : (sensor?.latestValue ?? null);
}

/**
 * Returns the unit string for a given logical measurement name.
 */
export function measurementUnit(name) {
    const units = {
        airTemp: '°C', humidity: '%', windSpeed: 'm/s', pressure: 'hPa',
        precipitation: 'mm', soilMoisture: '%', soilTemp: '°C', soilEC: 'dS/m',
        ph: 'pH', waterEC: 'mS/cm', dissolvedO2: 'mg/L', orp: 'mV',
        salinity: 'ppt', flow: 'L',
    };
    return units[name] ?? '';
}

/**
 * Find the weather sensor for a field's sensor list.
 * Matches both new sensorType.code === 'WEATHER' and legacy type string.
 */
export function findWeatherSensor(sensors) {
    return sensors?.find(s =>
        s.sensorType?.code === 'WEATHER' ||
        s.type === 'WEATHER' ||
        s.type === 'Temperature' ||
        s.type === 'WeatherStation' ||
        /meteo|weather|istasyon|station/i.test(s.name || '')
    ) ?? null;
}

/**
 * Extract ET₀ inputs from the field's sensor list.
 * Returns { Tmax, RH, uz } with nulls for missing values.
 */
export function extractEt0Inputs(sensors) {
    const weather = findWeatherSensor(sensors);

    const Tmax = readMeasurement(weather, 'airTemp');
    const RH   = readMeasurement(weather, 'humidity');

    // Wind speed: combine east + north components (vector magnitude) or use whichever is available
    const east  = weather?.latestData?.['meter_0_1_east_wind_speed'];
    const north = weather?.latestData?.['meter_0_1_north_wind_speed'];
    let uz = null;
    if (east != null && north != null) {
        uz = Math.round(Math.sqrt(east ** 2 + north ** 2) * 100) / 100;
    } else {
        uz = readMeasurement(weather, 'windSpeed');
    }

    return { Tmax, RH, uz };
}
